import { computed, ref, type DirectiveBinding } from 'vue'
import { defineStore } from 'pinia'
import { http } from '@/helpers/http'
import { useUserStore } from '@/stores/user'
import { type TTourStep } from '@/types/tour'

function groupTourSteps(steps: TTourStep[]): Record<string, TTourStep[]> {
  const groups: Record<string, TTourStep[]> = {}

  for (const step of steps) {
    if (!step.active) continue

    const group = groups[step.group_name] ?? []

    group.push(step)
    groups[step.group_name] = group
  }

  for (const group of Object.values(groups)) {
    group.sort((a, b) => a.order - b.order)
  }

  return groups
}

export const useTourStore = defineStore('tour', () => {
  const user = useUserStore()

  const completionKeyByGroup: Record<string, number> = {
    onboarding: user.userSettingLabels.ONBOARDING_COMPLETED,
    presentationEditor: user.userSettingLabels.PRESENTATION_TOUR_SHOWN,
    narrationStudio: user.userSettingLabels.NARRATION_TOUR_SHOWN,
  }

  const steps = ref<TTourStep[]>([])
  const tours = computed<Record<string, TTourStep[]>>(() => groupTourSteps(steps.value))

  const visible = ref(false)
  const gap = ref({ x: 0, y: 0, w: 0, h: 0 })
  const backdrop = ref<{ width: number; height: number }>({ width: 0, height: 0 })
  const direction = ref<
    'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | 'left' | 'right'
  >('top-center')

  const list = ref<{ element: HTMLElement; id: string; group: string }[]>([])
  const index = ref(0)
  const item = ref<TTourStep | null>(null)
  const currentGroupName = ref<string>('onboarding')

  let stepsPromise: Promise<void> | null = null

  function loadSteps(): Promise<void> {
    stepsPromise ??= http.get<TTourStep[]>('/tours').then((res) => {
      steps.value = res ?? []
      list.value = list.value
        .map((entry) => ({ ...entry, group: getGroupNameById(entry.id) }))
        .sort((a, b) => rankOf(a.id) - rankOf(b.id))
    })

    return stepsPromise
  }

  async function fetchSteps(): Promise<void> {
    steps.value = (await http.get<TTourStep[]>('/tours')) ?? []
  }

  async function createStep(step: Omit<TTourStep, 'id'>): Promise<TTourStep> {
    return http.post<TTourStep>('/tours', step)
  }

  async function updateStep(step: TTourStep): Promise<TTourStep> {
    return http.post<TTourStep>(`/tours/${step.id}`, step)
  }

  async function deleteStep(id: number): Promise<void> {
    await http.delete(`/tours/${id}`)
  }

  async function show(groupName: string) {
    await loadSteps()

    currentGroupName.value = groupName
    const currentItem = list.value.filter((item) => item.group === groupName)[index.value]

    if (!currentItem) return

    const currentElement = currentItem.element
    const sourceItem = tours.value[groupName]?.find((t) => t.name === currentItem.id) ?? null

    if (!currentElement || !sourceItem) return

    item.value = sourceItem

    document.body.classList.add('overflow-hidden')
    document.documentElement.scrollTop = 0

    const rect = currentElement.getBoundingClientRect()
    gap.value = {
      x: rect.left,
      y: rect.top,
      w: rect.width,
      h: rect.height,
    }

    const { width, height } = document.documentElement.getBoundingClientRect()
    backdrop.value = { width, height }

    const horizontalVariant = () => {
      if (rect.left < 200) return 'left'
      if (window.innerWidth - rect.left - rect.width < 200) return 'right'
      return 'center'
    }

    direction.value = (() => {
      const variant = horizontalVariant()

      if (rect.top < 100 && rect.bottom < window.innerHeight / 2) {
        if (variant === 'left') return 'top-left'
        if (variant === 'right') return 'top-right'
        return 'top-center'
      }

      if (rect.top > window.innerHeight - 260) {
        if (variant === 'left') return 'bottom-left'
        if (variant === 'right') return 'bottom-right'
        return 'bottom-center'
      }

      const spaceRight = window.innerWidth - rect.right

      return spaceRight >= rect.left ? 'left' : 'right'
    })()
    visible.value = true
  }

  function hide() {
    visible.value = false
    index.value = 0
    document.body.classList.remove('overflow-hidden')
  }

  async function showOnce(groupName: string) {
    const settingKey = completionKeyByGroup[groupName]

    if (settingKey === undefined) return

    if ((await user.getExtraSettingByKey(settingKey)) === '1') return

    await show(groupName)

    if (visible.value) user.saveExtraSetting(settingKey, '1')
  }

  function getGroupNameById(name: string): string {
    for (const [groupName, group] of Object.entries(tours.value)) {
      if (group.some((step) => step.name === name)) {
        return groupName
      }
    }

    return ''
  }

  function rankOf(id: string): number {
    let groupIndex = 0

    for (const group of Object.values(tours.value)) {
      const itemIndex = group.findIndex((step) => step.name === id)

      if (itemIndex !== -1) return groupIndex * 1000 + itemIndex

      groupIndex++
    }

    return Number.MAX_SAFE_INTEGER
  }

  function add(el: HTMLElement, binding: DirectiveBinding) {
    if (!binding.value) return

    list.value.push({ element: el, id: binding.value, group: getGroupNameById(binding.value) })
    list.value.sort((a, b) => rankOf(a.id) - rankOf(b.id))
  }

  function remove(el: HTMLElement) {
    list.value = list.value.filter((item) => item.element !== el)
  }

  function next() {
    index.value += 1
    show(currentGroupName.value)
  }

  function prev() {
    index.value -= 1
    show(currentGroupName.value)
  }

  window.addEventListener('resize', () => {
    if (!visible.value) return

    const { width, height } = document.documentElement.getBoundingClientRect()
    backdrop.value = { width, height }
    show(currentGroupName.value)
  })

  return {
    visible,
    currentGroupName,
    direction,
    gap,
    item,
    list,
    steps,
    current: index,
    backdrop,
    show,
    showOnce,
    hide,
    add,
    remove,
    next,
    prev,
    fetchSteps,
    createStep,
    updateStep,
    deleteStep,
  }
})
